import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ChakraProvider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  DrawerProps,
} from '@chakra-ui/react';
import { callV2Theme } from '@web-app/theming/theme';
import { FC, useRef, useState } from 'react';

export interface SessionActivityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isDirty: boolean;
  header: (onRequestClose: () => void) => React.ReactNode;
  footer: (onRequestClose: () => void) => React.ReactNode;
  children: React.ReactNode;
  confirmTitle?: string;
  confirmBody?: string;
  onConfirmClose?: () => Promise<void>;
  placement?: DrawerProps['placement'];
}

export const SessionActivityDrawer: FC<SessionActivityDrawerProps> = ({
  isOpen,
  onClose,
  isDirty,
  header,
  footer,
  children,
  confirmTitle = 'Leave without saving?',
  confirmBody = 'Any unsaved changes will be lost.',
  onConfirmClose,
  placement = 'right',
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleRequestClose = () => {
    if (isDirty) {
      setIsConfirmOpen(true);
    } else {
      onClose();
    }
  };
  const handleConfirmClose = async () => {
    if (onConfirmClose) {
      setIsSaving(true);
      try {
        await onConfirmClose();
      } finally {
        setIsSaving(false);
      }
    }
    setIsConfirmOpen(false);
    onClose();
  };
  const handleCancelClose = () => setIsConfirmOpen(false);

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement={placement}
        onClose={handleRequestClose}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent>
          <ChakraProvider theme={callV2Theme}>
            {header(handleRequestClose)}
            <DrawerBody overscrollBehavior="contain">{children}</DrawerBody>
            <DrawerFooter borderTopWidth="1px">
              {footer(handleRequestClose)}
            </DrawerFooter>
          </ChakraProvider>
        </DrawerContent>
      </Drawer>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmTitle}
            </AlertDialogHeader>
            <AlertDialogBody>{confirmBody}</AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={handleCancelClose}
                isDisabled={isSaving}
              >
                Stay
              </Button>
              <Button
                bg="interactive.brand.default"
                color="text.inverse"
                _hover={{ bg: 'interactive.brand.hover' }}
                _active={{ bg: 'interactive.brand.active' }}
                onClick={handleConfirmClose}
                isLoading={isSaving}
                ml={3}
              >
                {onConfirmClose ? 'Save & Close' : 'Leave'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

SessionActivityDrawer.displayName = 'SessionActivityDrawer';
